import classNames from 'classnames';
import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { LockIcon } from '../icons';
import styles from './Tabs.module.css';

export interface TabItem<T = string> {
  name: T;
  container: JSX.Element | React.ReactNode | string;
  label?: string;
  disabled?: boolean;
  disabledReason?: string;
  isError?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  activeTab: string;
}

export const Tabs = ({ activeTab, items }: TabsProps) => {
  return (
    <>
      {
        items
          .filter(i => i.name === activeTab)
          .map(item => <React.Fragment key={item.name}>{item.container}</React.Fragment>)[0]
      }
    </>
  );
};

export interface TabButtonsProps<T = string> {
  disabledTooltipId?: string;
  tabs: TabItem<T>[];
  activeTab?: string;
  tabNameParam?: string;
  setActiveTab?: Dispatch<SetStateAction<T>> | ((tab: T) => void);
  className?: string;
}

export const TabButtons = <T extends string>({
  disabledTooltipId = '',
  tabs,
  activeTab,
  setActiveTab,
  tabNameParam,
  className,
}: TabButtonsProps<T>) => {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();

  const handleChangeTab = useCallback(
    (nameTab: T) => () => {
      if (setActiveTab) {
        setActiveTab(nameTab);
        if (tabNameParam) {
          params.set(tabNameParam, nameTab as string);
          setParams(params, { state });
        }
      }
    },
    [params, setActiveTab, setParams, state, tabNameParam],
  );

  return (
    <div className={classNames('w-full max-w-full min-w-0 select-none', className)}>
      <div className="flex w-max min-w-max gap-4 items-center">
        {tabs.map(({ name, label, disabled, disabledReason, isError }, i) => (
          <button
            key={`tab_${i}_${name}`}
            data-tooltip-id={disabledTooltipId}
            data-tooltip-html={disabled ? disabledReason : ''}
            disabled={disabled}
            className={classNames(
              'flex items-center shrink-0 text-sm leading-[22px] font-medium relative',
              {
                'text-gray-500': activeTab !== name,
                'rounded-underline': activeTab === name,
                'bg-red-700 bg-opacity-80 !text-black px-2 rounded-lg':
                  isError && activeTab !== name,
                [styles.jumpAnimation]: isError && activeTab !== name,
              },
            )}
            onClick={handleChangeTab(name)}
          >
            <span>{label || (name as string)}</span>
            {disabled && (
              <img className="inline ml-1" width={16} height={16} src={LockIcon} alt="disabled" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
